import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getDiscountPreview,
  updateDiscountOverrides,
  resetDiscountOverrides,
} from "../../services/api";
import { DiscountPreview, CustomDiscount } from "../../types";

/** #11: Extract debounce constant */
const DISCOUNT_DEBOUNCE_MS = 800;

interface DiscountOverridesSectionProps {
  offerId: string;
  /** #4: Read-only mode for published offers */
  isReadOnly?: boolean;
}

/** Helper to calculate total discount from all components (#3) */
const calculateTotal = (
  p: number,
  s: number,
  w: number,
  cd: CustomDiscount[]
) => p + s + w + cd.reduce((sum, c) => sum + c.percentage, 0);

/** Confirmation dialog for switching from Manual back to Auto mode */
const ResetToAutoDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  /** #7: Escape key handler */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-dialog-title"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 id="reset-dialog-title" className="text-lg font-semibold mb-4">
          Przywróć rabaty automatyczne?
        </h3>
        <div className="space-y-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="text-yellow-600 mt-0.5">&#9888;&#65039;</div>
              <div>
                <p className="text-yellow-800 font-medium mb-2">
                  Ta operacja jest nieodwracalna
                </p>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>
                    Wszystkie ręczne nadpisania rabatów zostaną trwale usunięte
                  </li>
                  <li>
                    Rabaty wrócą do automatycznej kalkulacji z konfiguracji
                    hotelu
                  </li>
                  <li>
                    Custom rabaty (np. lojalnościowy) zostaną usunięte
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Resetowanie...
              </>
            ) : (
              "Przywróć automatyczne"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const DiscountOverridesSection: React.FC<DiscountOverridesSectionProps> = ({
  offerId,
  isReadOnly,
}) => {
  const [preview, setPreview] = useState<DiscountPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Manual mode state
  const [isManualMode, setIsManualMode] = useState(false);
  const [personDiscount, setPersonDiscount] = useState(0);
  const [stayLengthDiscount, setStayLengthDiscount] = useState(0);
  const [weekdayDiscount, setWeekdayDiscount] = useState(0);
  const [customDiscounts, setCustomDiscounts] = useState<CustomDiscount[]>([]);

  // New custom discount form
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomName, setNewCustomName] = useState("");
  const [newCustomPercentage, setNewCustomPercentage] = useState(0);

  // #1: Divergence warning dismissed state
  const [isDivergentDismissed, setIsDivergentDismissed] = useState(false);

  // #5: Success feedback state
  const [showSaved, setShowSaved] = useState(false);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>("");

  // #2: Pending save arguments ref for flush on unmount
  const pendingSaveRef = useRef<{
    person: number;
    stayLength: number;
    weekday: number;
    custom: CustomDiscount[];
  } | null>(null);

  const loadPreview = useCallback(async () => {
    try {
      setError(null);
      const data = await getDiscountPreview(offerId);
      setPreview(data);

      // #1: Reset divergence dismissal when preview changes
      setIsDivergentDismissed(false);

      // Set manual mode if overrides exist
      if (data.override) {
        setIsManualMode(true);
        setPersonDiscount(data.override.personDiscount);
        setStayLengthDiscount(data.override.stayLengthDiscount);
        setWeekdayDiscount(data.override.weekdayDiscount);
        setCustomDiscounts(data.override.customDiscounts || []);
      } else {
        setIsManualMode(false);
        // Pre-fill with auto values for when user switches to manual
        setPersonDiscount(data.auto.personDiscount);
        setStayLengthDiscount(data.auto.stayLengthDiscount);
        setWeekdayDiscount(data.auto.weekdayDiscount);
        setCustomDiscounts([]);
      }
    } catch (err: any) {
      console.error("Error loading discount preview:", err);
      setError(
        err.response?.data?.message || "Błąd podczas ładowania rabatów"
      );
    } finally {
      setIsLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // #2: Cleanup debounce timer on unmount with flush
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // #2: Flush pending save on unmount
      if (pendingSaveRef.current !== null) {
        const { person, stayLength, weekday, custom } =
          pendingSaveRef.current;
        updateDiscountOverrides(offerId, {
          personDiscount: person,
          stayLengthDiscount: stayLength,
          weekdayDiscount: weekday,
          customDiscounts: custom.length > 0 ? custom : undefined,
        }).catch((err) =>
          console.error("Error flushing pending discount save:", err)
        );
        pendingSaveRef.current = null;
      }
      // #5: Clean up saved feedback timeout
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, [offerId]);

  /** Save discount overrides with debounce */
  const debouncedSave = useCallback(
    (
      person: number,
      stayLength: number,
      weekday: number,
      custom: CustomDiscount[]
    ) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // #2: Store pending save args
      pendingSaveRef.current = { person, stayLength, weekday, custom };

      const payload = JSON.stringify({
        person,
        stayLength,
        weekday,
        custom,
      });

      // Skip if nothing changed
      if (payload === lastSavedRef.current) {
        pendingSaveRef.current = null;
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        setIsSaving(true);
        setError(null);
        try {
          await updateDiscountOverrides(offerId, {
            personDiscount: person,
            stayLengthDiscount: stayLength,
            weekdayDiscount: weekday,
            customDiscounts: custom.length > 0 ? custom : undefined,
          });
          lastSavedRef.current = payload;
          // #2: Clear pending save after success
          pendingSaveRef.current = null;

          // #5: Show saved feedback
          setShowSaved(true);
          if (savedTimeoutRef.current) {
            clearTimeout(savedTimeoutRef.current);
          }
          savedTimeoutRef.current = setTimeout(
            () => setShowSaved(false),
            2000
          );

          // Reload preview to get fresh divergence info
          const freshPreview = await getDiscountPreview(offerId);
          setPreview(freshPreview);
          // #1: Reset divergence dismissal when preview changes
          setIsDivergentDismissed(false);
        } catch (err: any) {
          console.error("Error saving discount overrides:", err);
          const message =
            err.response?.data?.error === "DISCOUNT_SUM_EXCEEDS_100"
              ? "Suma rabatów nie może przekraczać 100%"
              : err.response?.data?.message ||
                "Błąd podczas zapisywania rabatów";
          setError(message);
        } finally {
          setIsSaving(false);
        }
      }, DISCOUNT_DEBOUNCE_MS);
    },
    [offerId]
  );

  /** Handle switching to manual mode */
  const handleSwitchToManual = () => {
    if (!preview) return;
    setIsManualMode(true);

    // Pre-fill with auto values
    const person = preview.auto.personDiscount;
    const stayLength = preview.auto.stayLengthDiscount;
    const weekday = preview.auto.weekdayDiscount;

    setPersonDiscount(person);
    setStayLengthDiscount(stayLength);
    setWeekdayDiscount(weekday);
    setCustomDiscounts([]);

    // Save immediately to lock discounts (total is always <= 100 from auto)
    debouncedSave(person, stayLength, weekday, []);
  };

  /** Handle switching back to auto mode */
  const handleSwitchToAuto = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await resetDiscountOverrides(offerId);
      setIsManualMode(false);
      setCustomDiscounts([]);
      lastSavedRef.current = "";
      await loadPreview();
      setShowResetDialog(false);
    } catch (err: any) {
      console.error("Error resetting discounts:", err);
      setError(
        err.response?.data?.message || "Błąd podczas resetowania rabatów"
      );
    } finally {
      setIsSaving(false);
    }
  };

  /** #1: Handle "Przywroc automatyczne" from divergence warning */
  const handleDivergenceReset = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await resetDiscountOverrides(offerId);
      setIsManualMode(false);
      setCustomDiscounts([]);
      lastSavedRef.current = "";
      await loadPreview();
    } catch (err: any) {
      console.error("Error resetting discounts from divergence:", err);
      setError(
        err.response?.data?.message || "Błąd podczas resetowania rabatów"
      );
    } finally {
      setIsSaving(false);
    }
  };

  /** Handle discount value change */
  const handleDiscountChange = (
    field: "person" | "stayLength" | "weekday",
    value: number
  ) => {
    const clampedValue = Math.min(100, Math.max(0, value));

    let newPerson = personDiscount;
    let newStayLength = stayLengthDiscount;
    let newWeekday = weekdayDiscount;

    if (field === "person") {
      newPerson = clampedValue;
      setPersonDiscount(clampedValue);
    } else if (field === "stayLength") {
      newStayLength = clampedValue;
      setStayLengthDiscount(clampedValue);
    } else {
      newWeekday = clampedValue;
      setWeekdayDiscount(clampedValue);
    }

    // #3: Block save when total > 100%
    if (
      calculateTotal(newPerson, newStayLength, newWeekday, customDiscounts) <=
      100
    ) {
      debouncedSave(newPerson, newStayLength, newWeekday, customDiscounts);
    }
  };

  /** Add custom discount */
  const handleAddCustomDiscount = () => {
    if (!newCustomName.trim() || newCustomName.trim().length < 3) return;
    if (customDiscounts.length >= 10) return;

    const newDiscount: CustomDiscount = {
      name: newCustomName.trim(),
      percentage: Math.min(100, Math.max(0, newCustomPercentage)),
    };

    const updatedCustom = [...customDiscounts, newDiscount];
    setCustomDiscounts(updatedCustom);
    setNewCustomName("");
    setNewCustomPercentage(0);
    setShowAddCustom(false);

    // #3: Block save when total > 100%
    if (
      calculateTotal(
        personDiscount,
        stayLengthDiscount,
        weekdayDiscount,
        updatedCustom
      ) <= 100
    ) {
      debouncedSave(
        personDiscount,
        stayLengthDiscount,
        weekdayDiscount,
        updatedCustom
      );
    }
  };

  /** Remove custom discount */
  const handleRemoveCustomDiscount = (index: number) => {
    const updatedCustom = customDiscounts.filter((_, i) => i !== index);
    setCustomDiscounts(updatedCustom);

    // #3: Block save when total > 100%
    if (
      calculateTotal(
        personDiscount,
        stayLengthDiscount,
        weekdayDiscount,
        updatedCustom
      ) <= 100
    ) {
      debouncedSave(
        personDiscount,
        stayLengthDiscount,
        weekdayDiscount,
        updatedCustom
      );
    }
  };

  /** Update custom discount */
  const handleUpdateCustomDiscount = (
    index: number,
    field: "name" | "percentage",
    value: string | number
  ) => {
    const updatedCustom = customDiscounts.map((cd, i) => {
      if (i !== index) return cd;
      if (field === "name") return { ...cd, name: value as string };
      return {
        ...cd,
        percentage: Math.min(100, Math.max(0, value as number)),
      };
    });
    setCustomDiscounts(updatedCustom);

    // #9: Only auto-save custom name when length >= 3
    if (field === "name") {
      if ((value as string).trim().length >= 3) {
        // #3: Block save when total > 100%
        if (
          calculateTotal(
            personDiscount,
            stayLengthDiscount,
            weekdayDiscount,
            updatedCustom
          ) <= 100
        ) {
          debouncedSave(
            personDiscount,
            stayLengthDiscount,
            weekdayDiscount,
            updatedCustom
          );
        }
      }
    } else {
      // #3: Block save when total > 100%
      if (
        calculateTotal(
          personDiscount,
          stayLengthDiscount,
          weekdayDiscount,
          updatedCustom
        ) <= 100
      ) {
        debouncedSave(
          personDiscount,
          stayLengthDiscount,
          weekdayDiscount,
          updatedCustom
        );
      }
    }
  };

  /** Calculate total discount */
  const totalDiscount =
    personDiscount +
    stayLengthDiscount +
    weekdayDiscount +
    customDiscounts.reduce((sum, cd) => sum + cd.percentage, 0);

  if (isLoading) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Rabaty na noclegi</h3>
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-600">Ładowanie rabatów...</span>
        </div>
      </div>
    );
  }

  // #4: Read-only mode for published offers
  if (isReadOnly) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Rabaty na noclegi
        </h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-blue-700 text-sm">
            Rabaty są zamrożone po publikacji oferty
          </p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="space-y-4">
            <DiscountRow
              label="Rabat za liczbę osób"
              value={preview?.override?.personDiscount ?? preview?.auto.personDiscount ?? 0}
              autoValue={preview?.auto.personDiscount ?? 0}
              isManual={false}
              onChange={() => {}}
            />
            <DiscountRow
              label="Rabat za długość pobytu"
              value={preview?.override?.stayLengthDiscount ?? preview?.auto.stayLengthDiscount ?? 0}
              autoValue={preview?.auto.stayLengthDiscount ?? 0}
              isManual={false}
              onChange={() => {}}
            />
            <DiscountRow
              label="Rabat za pobyt w tygodniu"
              value={preview?.override?.weekdayDiscount ?? preview?.auto.weekdayDiscount ?? 0}
              autoValue={preview?.auto.weekdayDiscount ?? 0}
              isManual={false}
              onChange={() => {}}
            />
            {preview?.override?.customDiscounts?.map((cd, index) => (
              <div
                key={`${cd.name}-${index}`}
                className="flex items-center justify-between pl-4 border-l-2 border-blue-200"
              >
                <span className="text-sm font-medium text-gray-700">
                  {cd.name}
                </span>
                <span className="text-sm text-gray-600 font-medium">
                  {cd.percentage}%
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="font-medium text-gray-700">Suma rabatów:</span>
            <span className="text-lg font-bold text-gray-900">
              {preview?.auto.totalDiscount ?? 0}%
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Rabaty na noclegi
        </h3>
        <div className="flex items-center space-x-2">
          {isSaving && (
            <span className="text-xs text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-1"></div>
              Zapisywanie...
            </span>
          )}
          {/* #5: Success feedback */}
          {showSaved && !isSaving && (
            <span className="text-xs text-green-600 flex items-center">
              &#10003; Zapisano
            </span>
          )}
          {/* Auto/Manual toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={isManualMode ? handleSwitchToAuto : undefined}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                !isManualMode
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Auto
            </button>
            <button
              onClick={!isManualMode ? handleSwitchToManual : undefined}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isManualMode
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Ręczne
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* #1: Divergence warning with action buttons */}
      {isManualMode && preview?.isDivergent && !isDivergentDismissed && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-amber-800 text-sm font-medium mb-1">
            Ręczne rabaty różnią się od automatycznych
          </p>
          <p className="text-amber-700 text-xs mb-2">
            Automatycznie obliczone rabaty: osobowy{" "}
            {preview.auto.personDiscount}%, długość{" "}
            {preview.auto.stayLengthDiscount}%, weekday{" "}
            {preview.auto.weekdayDiscount}%
          </p>
          <div className="flex space-x-2">
            <button
              onClick={handleDivergenceReset}
              disabled={isSaving}
              className="px-3 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 disabled:opacity-50"
            >
              Przywróć automatyczne
            </button>
            <button
              onClick={() => setIsDivergentDismissed(true)}
              className="px-3 py-1 bg-white border border-amber-300 text-amber-800 rounded text-xs hover:bg-amber-100"
            >
              Zostaw ręczne
            </button>
          </div>
        </div>
      )}

      {/* Discount fields */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="space-y-4">
          {/* Person discount */}
          <DiscountRow
            label="Rabat za liczbę osób"
            value={isManualMode ? personDiscount : (preview?.auto.personDiscount ?? 0)}
            autoValue={preview?.auto.personDiscount ?? 0}
            details={
              preview?.auto.personDiscountDetails
                ? `>= ${preview.auto.personDiscountDetails.appliedTier.minPersons} os.`
                : undefined
            }
            isManual={isManualMode}
            onChange={(v) => handleDiscountChange("person", v)}
          />

          {/* Stay length discount */}
          <DiscountRow
            label="Rabat za długość pobytu"
            value={
              isManualMode
                ? stayLengthDiscount
                : (preview?.auto.stayLengthDiscount ?? 0)
            }
            autoValue={preview?.auto.stayLengthDiscount ?? 0}
            details={
              preview?.auto.stayLengthDiscountDetails
                ? `>= ${preview.auto.stayLengthDiscountDetails.appliedTier.minNights} noce`
                : undefined
            }
            isManual={isManualMode}
            onChange={(v) => handleDiscountChange("stayLength", v)}
          />

          {/* Weekday discount */}
          <DiscountRow
            label="Rabat za pobyt w tygodniu"
            value={
              isManualMode
                ? weekdayDiscount
                : (preview?.auto.weekdayDiscount ?? 0)
            }
            autoValue={preview?.auto.weekdayDiscount ?? 0}
            details={
              preview?.auto.weekdayDiscountDetails?.isWeekdayOnly
                ? "tylko dni robocze"
                : undefined
            }
            isManual={isManualMode}
            onChange={(v) => handleDiscountChange("weekday", v)}
          />

          {/* Custom discounts */}
          {isManualMode && (
            <>
              {/* #12: Fix React key for customDiscounts */}
              {customDiscounts.map((cd, index) => (
                <div
                  key={`${cd.name}-${index}`}
                  className="flex items-center space-x-3 pl-4 border-l-2 border-blue-200"
                >
                  {/* #6: aria-label for custom discount name input */}
                  <input
                    type="text"
                    value={cd.name}
                    onChange={(e) =>
                      handleUpdateCustomDiscount(index, "name", e.target.value)
                    }
                    className="flex-1 p-2 border border-gray-300 rounded text-sm"
                    placeholder="Nazwa rabatu..."
                    minLength={3}
                    maxLength={100}
                    aria-label="Nazwa rabatu niestandardowego"
                  />
                  <div className="flex items-center space-x-1">
                    {/* #6: aria-label for custom discount percentage input */}
                    <input
                      type="number"
                      value={cd.percentage}
                      onChange={(e) =>
                        handleUpdateCustomDiscount(
                          index,
                          "percentage",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-20 p-2 border border-gray-300 rounded text-sm text-center"
                      min={0}
                      max={100}
                      step={1}
                      aria-label={cd.name + " - wartość procentowa"}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  {/* #6: aria-label for delete button */}
                  <button
                    onClick={() => handleRemoveCustomDiscount(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title={`Usuń rabat: ${cd.name}`}
                    aria-label={`Usuń rabat: ${cd.name}`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}

              {/* Add custom discount form */}
              {showAddCustom ? (
                <div className="flex flex-col space-y-1 pl-4 border-l-2 border-green-200">
                  <div className="flex items-center space-x-3">
                    {/* #6: aria-label for new custom name input */}
                    <input
                      type="text"
                      value={newCustomName}
                      onChange={(e) => setNewCustomName(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded text-sm"
                      placeholder="Nazwa rabatu (min. 3 znaki)..."
                      minLength={3}
                      maxLength={100}
                      autoFocus
                      aria-label="Nazwa nowego rabatu"
                    />
                    <div className="flex items-center space-x-1">
                      {/* #6: aria-label for new custom percentage input */}
                      <input
                        type="number"
                        value={newCustomPercentage}
                        onChange={(e) =>
                          setNewCustomPercentage(
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="w-20 p-2 border border-gray-300 rounded text-sm text-center"
                        min={0}
                        max={100}
                        step={1}
                        aria-label="Wartość procentowa nowego rabatu"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                    <button
                      onClick={handleAddCustomDiscount}
                      disabled={
                        !newCustomName.trim() ||
                        newCustomName.trim().length < 3
                      }
                      className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                      Dodaj
                    </button>
                    <button
                      onClick={() => {
                        setShowAddCustom(false);
                        setNewCustomName("");
                        setNewCustomPercentage(0);
                      }}
                      className="px-3 py-2 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                    >
                      Anuluj
                    </button>
                  </div>
                  {/* #10: Inline validation hint for custom name min length */}
                  {newCustomName.length > 0 && newCustomName.trim().length < 3 && (
                    <p className="text-xs text-amber-600 ml-0">
                      Minimalna długość nazwy to 3 znaki
                    </p>
                  )}
                </div>
              ) : (
                customDiscounts.length < 10 && (
                  <button
                    onClick={() => setShowAddCustom(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1 pl-4"
                  >
                    <span>+</span>
                    <span>Dodaj rabat niestandardowy</span>
                  </button>
                )
              )}
            </>
          )}
        </div>

        {/* Total discount summary */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
          <span className="font-medium text-gray-700">Suma rabatów:</span>
          <span
            className={`text-lg font-bold ${
              totalDiscount > 100 ? "text-red-600" : "text-gray-900"
            }`}
          >
            {isManualMode
              ? totalDiscount
              : (preview?.auto.totalDiscount ?? 0)}
            %
          </span>
        </div>
        {totalDiscount > 100 && isManualMode && (
          <p className="text-red-600 text-xs mt-1">
            Suma rabatów nie może przekraczać 100%
          </p>
        )}
      </div>

      {/* Reset confirmation dialog */}
      <ResetToAutoDialog
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        isLoading={isSaving}
      />
    </div>
  );
};

/** Single discount row - either read-only (auto) or editable (manual) */
const DiscountRow: React.FC<{
  label: string;
  value: number;
  autoValue: number;
  details?: string;
  isManual: boolean;
  onChange: (value: number) => void;
}> = ({ label, value, autoValue, details, isManual, onChange }) => {
  return (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {details && (
          <span className="text-xs text-gray-500 ml-2">({details})</span>
        )}
      </div>
      <div className="flex items-center space-x-2">
        {isManual ? (
          <>
            {/* #6: aria-label for discount row input */}
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
              className="w-20 p-2 border border-gray-300 rounded text-sm text-center"
              min={0}
              max={100}
              step={1}
              aria-label={label + " - wartość procentowa"}
            />
            <span className="text-sm text-gray-500">%</span>
            {value !== autoValue && (
              <span className="text-xs text-amber-600" title="Wartość auto">
                (auto: {autoValue}%)
              </span>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-600 font-medium">
            {value}%
            {value === 0 && (
              <span className="text-xs text-gray-400 ml-1">
                -- brak rabatu
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default DiscountOverridesSection;
